<script setup>

// Imports
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'

// Props
const emit = defineEmits(['loaded', 'error'])
const props = defineProps({
  fill: { type: Boolean, default: false },
  desktopUrl: {
    type: String,
    default: ''
  },
  mobileUrl: {
    type: String,
    default: ''
  },
  // Only the active slide plays, from the start each time; created the first time its slide is active
  active: {
    type: Boolean,
    default: false
  }
})

// Same breakpoint as the slide's desktop / mobile layout and video slots in slider.vue
const DESKTOP_QUERY = '(min-width: 1024px)'

// If only one file is set, it fills both slots
const desktopSrc = computed(() => props.desktopUrl || props.mobileUrl)
const mobileSrc = computed(() => props.mobileUrl || props.desktopUrl)

const canvas = ref(null)
const isDesktop = ref(true)
// The animation's own size (from the Lottie file); on desktop the canvas is drawn at this size
const naturalSize = ref(null)

const sizeStyle = computed(() => (naturalSize.value
  ? { '--lottie-width': `${naturalSize.value.width}px`, '--lottie-height': `${naturalSize.value.height}px` }
  : {}))

let player = null
let creating = null
let mediaQuery = null
let reducedMotion = false
let observer = null
let visible = false
let disposed = false
const canPlay = () => props.active && visible && !reducedMotion

const currentSrc = () => (isDesktop.value ? desktopSrc.value : mobileSrc.value)

const createPlayer = async () => {
  if (player || creating || !canvas.value || !currentSrc()) return

  creating = import('@lottiefiles/dotlottie-web').then(({ DotLottie }) => {
    if (disposed || !canvas.value) return

    player = new DotLottie({
      canvas: canvas.value,
      src: currentSrc(),
      // Reduced motion: show the first frame only
      autoplay: canPlay(),
      // Plays once per visit to the slide and holds the last frame; the next visit starts over
      loop: false,
      layout: { fit: 'contain', align: [0.5, 0.5] },
      renderConfig: { autoResize: true }
    })

    // Fires again when a breakpoint change loads the other file
    player.addEventListener('load', () => {
      const size = player?.animationSize()
      if (size?.width && size?.height) {
        naturalSize.value = { width: size.width, height: size.height }
        emit('loaded', naturalSize.value)
      }
      syncPlayback()
    })
    player.addEventListener('loadError', () => emit('error'))
  }).catch(() => { if (!disposed) emit('error') })

  await creating
  creating = null

  // The slide may have been switched away from while the library was loading
  if (player && !props.active) player.stop()
}

// Every time its slide becomes active the animation starts over; leaving the slide resets it
// to the first frame (stop), so it never resumes from the middle.
const syncPlayback = () => {
  if (!props.active || !visible) {
    player?.stop()
    return
  }

  if (!player) {
    createPlayer()
    return
  }

  player.stop()
  if (canPlay()) player.play()
}

// Crossing the breakpoint swaps the file when desktop and mobile differ
const onBreakpointChange = (event) => {
  isDesktop.value = event.matches
  if (!player || desktopSrc.value === mobileSrc.value) return

  player.load({
    src: currentSrc(),
    autoplay: canPlay(),
    loop: false,
    layout: { fit: 'contain', align: [0.5, 0.5] }
  })
}

watch(() => props.active, syncPlayback)
watch([desktopSrc, mobileSrc], () => {
  naturalSize.value = null
  if (!player) { syncPlayback(); return }
  player.load({ src: currentSrc(), autoplay: canPlay(), loop: false, layout: { fit: 'contain', align: [0.5, 0.5] } })
})

onMounted(() => {
  mediaQuery = window.matchMedia(DESKTOP_QUERY)
  isDesktop.value = mediaQuery.matches
  mediaQuery.addEventListener('change', onBreakpointChange)
  reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  observer = new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting
    syncPlayback()
  })
  observer.observe(canvas.value)
})

onBeforeUnmount(() => {
  disposed = true
  observer?.disconnect()
  mediaQuery?.removeEventListener('change', onBreakpointChange)
  player?.destroy()
  player = null
})

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
<div class="slide-lottie"
  :class="{ '--sized': naturalSize, '--fill': fill }"
  :style="sizeStyle"
  aria-hidden="true">
  <canvas ref="canvas" class="slide-lottie__canvas"></canvas>
</div>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

/*
  Over the slide image, centred. Mobile: fitted into the slide minus 28px on each side.
  Desktop: drawn at the animation's own size, shrunk only if it would overflow the slide.
  (No var() fallbacks: postcss-custom-properties runs with preserve: false and would inline them.)
*/
.slide-lottie {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 28px;
  pointer-events: none;
}

.slide-lottie__canvas {
  display: block;
  width: 100%;
  height: 100%;
}

@media (min-width: 1024px) {
  .slide-lottie {
    padding: 0;
  }

  .slide-lottie.--sized .slide-lottie__canvas {
    width: var(--lottie-width);
    height: var(--lottie-height);
    max-width: 100%;
    max-height: 100%;
  }
}

.slide-lottie.--fill { padding: 0; }
.slide-lottie.--fill .slide-lottie__canvas { width: 100%; height: 100%; }
</style>
